import Vue from 'vue'
import store from 'store'
import { mapState, mapActions } from 'vuex'
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock'
import { formatPrice } from 'utils/general'
import accessibility from 'mixins/accessibility'

export default el => new Vue({
  el,
  store,
  data () {
    return {
      noteExpanded: false,
      message: '',
      complimentRequestId: 0,
      complimentRenderedProductId: ''
    }
  },
  mixins: [accessibility],
  mounted () {
    this.message = this.cart.note

    // The initial cart is server-rendered into __GLOBAL__, so `getCart` never
    // runs on a plain page load and the despatch data would stay empty until
    // the first cart mutation.
    if (this.cartItems.length) {
      this.fetchDeliveryData()
    }
  },
  computed: {
    ...mapState('global', [
      'cart'
    ]),
    ...mapState('cart', [
      'open',
      'loading'
    ]),
    cartStatus () {
      return this.open
    },
    cartItems () {
      return (this.cart.items || [])
    },
    visibleCartItems () {
      const opts = __GLOBAL__.packagingOptions
      const packagingIds = opts && opts.enabled
        ? [opts.free && opts.free.id, opts.premium && opts.premium.id].filter(Boolean)
        : []
      return this.cartItems.filter(item =>
        __GLOBAL__.engravingItem.id !== item.id &&
        !packagingIds.includes(item.variant_id)
      )
    },
    cartQuantity () {
      let qty = 0
      const items = this.visibleCartItems
      for (let i = 0; i < items.length; i++) {
        qty = qty + items[i].quantity
      }

      return qty
    },
    isEmpty () {
      return this.visibleCartItems.length === 0
    },
    cartTotal () {
      return this.priceFormat(this.cart.total_price)
    },
    isActive () {
      return this.open
    },
    cartTitleMeta () {
      if (this.cartQuantity === 0) {
        return '(0)'
      }

      return `- ${this.cartQuantity} ${this.cartQuantity === 1 ? 'item' : 'items'}`
    },
    complimentSeedProductId () {
      const seedItem = this.visibleCartItems[0]
      return seedItem && seedItem.product_id ? seedItem.product_id : ''
    },
    packagingOptions () {
      const options = __GLOBAL__.packagingOptions
      return options && options.enabled ? options : null
    },
    selectedPackaging () {
      if (!this.packagingOptions) return null
      const { free, premium } = this.packagingOptions
      const items = this.cart.items || []

      if (premium && premium.id && items.find(i => i.variant_id === premium.id)) return 'premium'
      if (free && free.id && items.find(i => i.variant_id === free.id)) return 'free'
      return null
    }
  },
  methods: {
    ...mapActions('cart', [
      'toggleCart',
      'closeCart',
      'syncPackaging',
      'fetchDeliveryData'
    ]),
    setPageScrollLock (locked) {
      document.documentElement.classList.toggle('no-scroll', locked)
      document.body.classList.toggle('no-scroll', locked)
    },
    toggleBackgroundLock () {
      this.setPageScrollLock(this.isActive)

      if (this.isActive && this.$refs.container) {
        disableBodyScroll(this.$refs.container)
      }

      if (!this.isActive) {
        clearAllBodyScrollLocks()

        if (this.$refs.container) {
          enableBodyScroll(this.$refs.container)
        }
      }
    },
    async syncComplimentRecommendations () {
      const complimentEl = this.$refs.compliment

      if (!complimentEl) return

      const productId = this.complimentSeedProductId
      if (!productId || this.isEmpty) {
        complimentEl.innerHTML = ''
        complimentEl.setAttribute('aria-hidden', 'true')
        this.complimentRenderedProductId = ''
        return
      }

      if (this.complimentRenderedProductId === String(productId) && complimentEl.innerHTML.trim() !== '') {
        return
      }

      const requestId = ++this.complimentRequestId
      const url = `${__GLOBAL__.routes.recommendationsUrl}?section_id=cart-compliment&product_id=${productId}&limit=3&intent=related`

      try {
        const response = await window.fetch(url, {
          credentials: 'same-origin'
        })

        if (!response.ok) return

        const html = await response.text()
        if (requestId !== this.complimentRequestId) return

        const parser = new window.DOMParser()
        const doc = parser.parseFromString(html, 'text/html')
        const section = doc.querySelector('#shopify-section-cart-compliment')
        const markup = section ? section.innerHTML.trim() : ''

        complimentEl.innerHTML = markup
        complimentEl.setAttribute('aria-hidden', markup ? 'false' : 'true')
        this.complimentRenderedProductId = markup ? String(productId) : ''
      } catch (error) {
        complimentEl.innerHTML = ''
        complimentEl.setAttribute('aria-hidden', 'true')
        this.complimentRenderedProductId = ''
      }
    },
    toggle () {
      this.toggleCart()
    },
    close () {
      this.closeCart()
      this.setPageScrollLock(false)
      clearAllBodyScrollLocks()

      if (this.$refs.container) {
        enableBodyScroll(this.$refs.container)
      }
    },
    priceFormat (price) {
      return formatPrice(price)
        .replace(/\bPLN\b/g, 'zl')
        .replace(/z\u0142/g, 'zl')
    },
    toggleNote () {
      this.noteExpanded = !this.noteExpanded
    },
    checkoutCart () {
      this.$refs.cartForm.submit()
    },
    selectPackaging (type) {
      // If already selected — deselect (toggle off)
      const next = this.selectedPackaging === type ? null : type
      this.syncPackaging(next)
    }
  },
  watch: {
    open () {
      this.$nextTick(() => {
        this.toggleBackgroundLock()
        this.syncComplimentRecommendations()
      })
    },
    complimentSeedProductId () {
      if (!this.open) return

      this.$nextTick(() => {
        this.syncComplimentRecommendations()
      })
    }
  },
  beforeDestroy () {
    this.setPageScrollLock(false)
    clearAllBodyScrollLocks()
  }
})
